import { useQuery } from '@tanstack/react-query';
import PaymentsAPIServiceV2, {
    AvailableShifts,
    CurrentPeriodShifts
} from './PaymentsServiceV2';
import { GetZenegyUsersDetailedReqest } from '@/models/payment';
import { useUser } from '@/contexts/user/UserContext';

const paymentsService = PaymentsAPIServiceV2.getInstance();

export const PAYMENT_QUERY_KEYS: { [key: string]: string } = {
    TIME_REGISTRATION_PROVIDERS: 'getTimeRegistrationProviders',
    SALARY_PROVIDERS: 'getSalaryProviders',
    EMPLOYEE_TYPES: 'getEmployeeTypes',
    EMPLOYEE_AUTO_APPROVAL_TYPE_SETTINGS: 'getEmployeeAutoApprovalTypeSettings',
    DEPARTMENTS: 'getDepartments',
    USER: 'getUser',
    syncBankTransactionRecords: 'syncBankTransactionRecords',
    getAllBankTransactionReports: 'getAllBankTransactionReports',
    getTransactionBankDetails: 'getTransactionBankDetails',
    getBankTransactionRecordDetails: 'getBankTransactionRecordDetails',
    getCompanyAccounts: 'getCompanyAccounts',
    getLast10Jobs: 'getLast10Jobs',
    getImportFilters: 'getImportFilters',
    getAllIntegrationStatus: 'getAllIntegrationStatus',
    getPendingTransactions: 'getPendingTransactions',
    getDanLonConnectURL: 'getDanLonConnectURL',
    getDanLonExchange: 'getDanLonExchange',
    getMasterCardConnectURL: 'getMasterCardConnectURL',
    getCurrentCompanyDetails: 'getCurrentCompanyDetails',
    getPOSLocationsAndDepartments: 'getPOSLocationsAndDepartments',
    getAllPOSDepartments: 'getAllPOSDepartments',
    getPOSJournalLines: 'getPOSJournalLines',
    getTrivecGroupFilters: 'getTrivecGroupFilters',
    getPOSEmployeeGroup: 'getPOSEmployeeGroup',
    getPOSProviders: 'getPOSProviders',
    getUserTips: 'getUserTips',
    getTipsExclusions: 'getTipsExclusions',
    getDepartmentSalaryTypes: 'getDepartmentSalaryTypes',
    getPayrollStatus: 'getPayrollStatus',
    getUserPastTips: 'getUserPastTips',
    getEmploymentGroupTemplates: 'getEmploymentGroupTemplates',
    getTipsHistory: 'getTipsHistory',
    getEmployeeDocuments: 'getEmployeeDocuments',
    getUserCompanies: 'getUserCompanies',
    getAdminCompanies: 'getAdminCompanies',
    ZENEGY_DEPARTMENTS: 'getZenegyDepartments',
    ZENEGY_SALARY_MODES: 'getZenegySalaryModes',
    ZENEGY_SALARY_TYPES: 'getZenegySalaryTypes',
    ZENEGY_EMPLOYEE_TYPES: 'getZenegyEmployeeTypes',
    INTECT_DEPARTMENTS: 'getIntectDepartments',
    INTECT_EMPLOYMENT_TEMPLATES: 'getIntectEmploymentTemplates',
    GET_UPCOMING_SHIFTS: 'getUpcomingShifts',
    GET_AVAILABLE_SHIFTS: 'getAvailableShifts'
};

export const useTimeRegistrationProvidersQuery = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.TIME_REGISTRATION_PROVIDERS, enabled],
        queryFn: paymentsService.getTimeRegistrationProviders,
        enabled
    });

export const useSalaryProvidersQuery = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.SALARY_PROVIDERS, enabled],
        queryFn: paymentsService.getSalaryProviders,
        enabled
    });

export const useEmployeeTypes = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.EMPLOYEE_TYPES, enabled],
        queryFn: paymentsService.getEmployeeTypes,
        enabled
    });

export const useEmployeeAutoApprovalTypesQuery = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.EMPLOYEE_AUTO_APPROVAL_TYPE_SETTINGS],
        queryFn: paymentsService.getEmployeeAutoApprovalTypes,
        enabled
    });

export const useEmployeeDepartments = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.DEPARTMENTS, enabled],
        queryFn: paymentsService.getEmployeeDepartments,
        enabled
    });

export const useGetUserQuery = ({
    userId,
    enabled
}: {
    userId?: string;
    enabled: boolean;
}) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.USER, userId],
        queryFn: () => paymentsService.getUser(userId!),
        enabled: !!userId && enabled
    });
};

/**
 * @deprecated Use useGetAllIntegrationStatus instead. This function name doesn't follow React hooks naming convention.
 */
export const getAllIntegrationStatus = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus, enabled],
        queryFn: paymentsService.getAllIntegrationStatus,
        enabled
    });

export const useGetAllIntegrationStatus = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus, enabled],
        queryFn: paymentsService.getAllIntegrationStatus,
        enabled
    });

export const useGetAllBankTransactionReports = ({
    enabled = true,
    limit,
    offset,
    statuses
}: {
    enabled?: boolean;
    limit?: number;
    offset?: number;
    statuses?: string[];
}) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getAllBankTransactionReports,
            enabled,
            limit,
            offset,
            statuses
        ],
        queryFn: () => {
            return paymentsService.getAllBankTransactionRecords(
                statuses,
                limit,
                offset
            );
        },
        enabled
    });
};

export const useSyncSyncBankTransactionRecords = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.syncBankTransactionRecords, enabled],
        queryFn: paymentsService.getSyncBankTransactionRecords
    });
};

export const useGetTransactionBankDetails = (
    bankTransactionRecordId: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getTransactionBankDetails,
            bankTransactionRecordId,
            enabled
        ],
        queryFn: () =>
            paymentsService.getTransactionBankDetails(bankTransactionRecordId),
        enabled
    });
};

export const useGetBankTransactionRecordDetails = (
    bankTransactionRecordId: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getBankTransactionRecordDetails,
            bankTransactionRecordId,
            enabled
        ],
        queryFn: () =>
            paymentsService.getBankTransactionRecordDetails(
                bankTransactionRecordId
            ),
        enabled: enabled && !!bankTransactionRecordId
    });
};

export const useGetCompanyAccounts = (enabled: boolean = true) => {
    return useQuery({
        queryKey: ['getCompanyAccounts', enabled],
        queryFn: paymentsService.getCompanyAccounts
    });
};

export const useGetImportFilters = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getImportFilters, enabled],
        queryFn: paymentsService.getImportFilters
    });
};

export const useGetImportFiltersForSource = (
    sourceType: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getImportFilters, sourceType, enabled],
        queryFn: () => paymentsService.getImportFiltersForSource(sourceType),
        enabled: enabled && !!sourceType
    });
};

export const useGetJobStatus = (jobId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: ['getJobStatus', jobId, enabled],
        queryFn: () => paymentsService.getJobStatus(jobId),
        enabled
    });
};

export const useGetLastJobs = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getLast10Jobs, enabled],
        queryFn: () => paymentsService.getLastJobs()
    });
};

// //TODO: remove other queries and use this one
// export const useGetCurrentCompany = (enabled: boolean = true) => {
//     return useQuery({
//         queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompany, enabled],
//         queryFn: () => paymentsService.getCurrentCompany()
//     });
// };

export const useGetEmployees = (
    request?: GetZenegyUsersDetailedReqest,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getUsersDetails', JSON.stringify(request)],
        queryFn: () =>
            paymentsService.getUsersDetails<GetZenegyUsersDetailedReqest>(
                request
            ),
        enabled
    });
};

export const useCompanyBalance = (enabled: boolean = true) => {
    const { company } = useUser();
    const companyId = company?.companyId;
    return useQuery({
        queryKey: ['getCompanyBalance', companyId],
        queryFn: paymentsService.getCurrentCompanyBalance,
        enabled: !!companyId && enabled
    });
};

export const useGetCompanyUsers = (
    limit: number,
    offset: number,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getCompanyUsers', limit, offset],
        queryFn: () => paymentsService.getCompanyUsers(limit, offset),
        enabled
    });
};

export const useGetPendingTransactions = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions, enabled],
        queryFn: () => paymentsService.getPendingTransactions(),
        enabled
    });
};

export const useDanLonConnectURL = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getDanLonConnectURL, enabled],
        queryFn: () => paymentsService.getDanLonConnectURL(),
        enabled
    });
};

export const useCurrentCompanyDetails = (enabled: boolean = true) => {
    const { company } = useUser();
    const companyId = company?.companyId;
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompanyDetails, companyId],
        queryFn: () => paymentsService.getCurrentCompany(),
        enabled: !!companyId && enabled
    });
};

export const useMasterCardConnectURL = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getMasterCardConnectURL, enabled],
        queryFn: () => paymentsService.getMasterCardConnectUrl(),
        enabled
    });
};

export const useDanLonExchange = (code: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getDanLonExchange, enabled],
        queryFn: () => paymentsService.dalonExchange(code)
    });
};

export const useGetPOSLocationsAndDepartments = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments, enabled],
        queryFn: () => paymentsService.getPOSLocationsAndDepartments(),
        enabled
    });
};

/**
 * @deprecated Use useGetAllPOSDepartments instead. This function name doesn't follow React hooks naming convention.
 */
export const getAllPOSDepartments = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getAllPOSDepartments, enabled],
        queryFn: paymentsService.getAllPOSDepartments,
        enabled
    });

export const useGetAllPOSDepartments = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getAllPOSDepartments, enabled],
        queryFn: paymentsService.getAllPOSDepartments,
        enabled
    });

export const useGetPOSJournalLines = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines, enabled],
        queryFn: paymentsService.getPOSJournalLines,
        enabled
    });

export const useGetTrivecGroupFilters = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getTrivecGroupFilters, enabled],
        queryFn: paymentsService.getTrivecGroupFilters,
        enabled
    });

/**
 * @deprecated Use useGetEmployeeGroup instead. This function name doesn't follow React hooks naming convention.
 */
export const getEmployeeGroup = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSEmployeeGroup, enabled],
        queryFn: paymentsService.getTipsEmployeeGroup,
        enabled
    });

export const useGetEmployeeGroup = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSEmployeeGroup, enabled],
        queryFn: paymentsService.getTipsEmployeeGroup,
        enabled
    });

/**
 * @deprecated Use useGetPOSProviders instead. This function name doesn't follow React hooks naming convention.
 */
export const getPOSProviders = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSProviders, enabled],
        queryFn: paymentsService.getPOSProviders,
        enabled
    });

export const useGetPOSProviders = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSProviders, enabled],
        queryFn: paymentsService.getPOSProviders,
        enabled
    });

/**
 * @deprecated Use useGetUserTips instead. This function name doesn't follow React hooks naming convention.
 */
export const getUserTips = (userId: string, enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getUserTips, userId, enabled],
        queryFn: () => paymentsService.getUserTips(userId),
        enabled
    });

export const useGetUserTips = (userId: string, enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getUserTips, userId, enabled],
        queryFn: () => paymentsService.getUserTips(userId),
        enabled
    });

export const useGetTipsExclusions = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getTipsExclusions, enabled],
        queryFn: paymentsService.getTipsExclusions,
        enabled
    });

export const useGetDepartmentSalaryTypes = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getDepartmentSalaryTypes, enabled],
        queryFn: paymentsService.getDepartmentSalaryTypes,
        enabled
    });

export const useGetPayrollStatus = (userId: string, enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPayrollStatus, userId, enabled],
        queryFn: async () => {
            const response = await paymentsService.getPayrollStatus(userId);
            return response.data;
        },
        enabled
    });

export const useGetUserPastTips = ({
    userId,
    startDate,
    endDate,
    offset,
    limit,
    enabled = true
}: {
    userId: string;
    startDate?: string;
    endDate?: string;
    offset?: number;
    limit?: number;
    enabled?: boolean;
}) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getUserPastTips,
            userId,
            startDate,
            endDate,
            offset,
            limit
        ],
        queryFn: () =>
            paymentsService.getUserPastTips(
                userId,
                startDate,
                endDate,
                offset,
                limit
            ),
        enabled
    });
};

export const useTipsHistory = (
    offset: number,
    limit: number,
    locationIds: string[],
    statuses?: string[],
    reportingStatuses?: string[],
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getTipsHistory,
            enabled,
            offset,
            limit,
            locationIds,
            statuses,
            reportingStatuses
        ],
        queryFn: ({ signal }) =>
            paymentsService.getTipsHistory({
                offset,
                limit,
                locationIds,
                statuses,
                reportingStatuses,
                signal
            }),
        enabled
    });
};

export const useTipsHistoryDetails = (
    posLocationId: string,
    date: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getTipsHistoryDetails', enabled, posLocationId, date],
        queryFn: () =>
            paymentsService.getTipsHistoryDetails({
                posLocationId,
                date
            })
    });
};

export const useTipsHistoryExcelDownload = (
    locationIds: string[],
    statuses: string[],
    reportingStatuses: string[],
    enabled: boolean = false
) => {
    return useQuery({
        queryKey: [
            'getTipsHistoryDownload',
            locationIds,
            statuses,
            reportingStatuses,
            enabled
        ],
        queryFn: () =>
            paymentsService.getTipsHistoryExcelDownload({
                locationIds,
                statuses,
                reportingStatuses
            }),
        enabled: enabled
    });
};

export const useTipsReconciliationReport = (
    month: string,
    year: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getTipsReconciliation', month, year, enabled],
        queryFn: () => paymentsService.getTipsReconciliationReport(month, year),
        enabled
    });
};

export const useEstimatedTipsReconciliationReport = (
    month: string,
    year: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getEstimatedTipsReconciliation', month, year, enabled],
        queryFn: () =>
            paymentsService.getEstimatedTipsReconciliationReport(month, year),
        enabled
    });
};

export const useActivePayrollPeriods = (enabled: boolean = true) => {
    return useQuery({
        queryKey: ['getActivePayrollPeriods', enabled],
        queryFn: () => paymentsService.getActivePayrollPeriods(),
        enabled
    });
};

export const useGetEmploymentGroupTemplates = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getEmploymentGroupTemplates, enabled],
        queryFn: () => paymentsService.getEmploymentGroupTemplates(),
        enabled
    });
};

export const useTransactionStatistics = (enabled: boolean = true) => {
    const { company } = useUser();
    const companyId = company?.companyId;
    return useQuery({
        queryKey: ['getTransactionStatistics', companyId],
        queryFn: paymentsService.getTransactionStatistics,
        enabled: !!companyId && enabled
    });
};

export const useGetEmployeeDocuments = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments, enabled],
        queryFn: paymentsService.getEmployeeDocuments,
        enabled
    });
};

export const useGetDocumentImage = (
    url: string,
    pageNumber: number,
    enabled: boolean
) => {
    return useQuery({
        queryKey: ['getDocumentImage', url, pageNumber],
        queryFn: () =>
            paymentsService.generateDocumentImage({ url, pageNumber }),
        enabled
    });
};

export const useGetSignatures = (
    limit: number = 16,
    offset: number = 0,
    searchQuery?: string,
    sortField?: string,
    sortDirection?: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [
            'getSignatures',
            limit,
            offset,
            searchQuery,
            sortField,
            sortDirection
        ],
        queryFn: () =>
            paymentsService.getSignatures(
                limit,
                offset,
                searchQuery,
                sortField,
                sortDirection
            ),
        enabled
    });
};

export const useGetUserCompanies = (
    userId: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getUserCompanies, userId],
        queryFn: () => paymentsService.getCompanyByUserId(userId),
        enabled: enabled && !!userId,
        select: (response) =>
            response?.data?.companies?.map((company: any) => ({
                id: company.companyId,
                name: company.name,
                role: company?.role
            })) || []
    });
};

export const useGetAdminCompanies = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getAdminCompanies],
        queryFn: () => paymentsService.getAdminCompanies(),
        enabled,
        select: (response) =>
            response?.data?.companies
                ?.map((company: any) => ({
                    id: company.companyId,
                    name: company.name
                }))
                .sort((a: any, b: any) => a.name?.localeCompare(b.name)) || []
    });
};

export const useZenegyDepartmentsQuery = (enabled: boolean = true) => {
    const { company } = useUser();
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.ZENEGY_DEPARTMENTS, company?.companyId],
        queryFn: paymentsService.getZenegyDepartments,
        enabled: !!company?.companyId && enabled
    });
};

export const useZenegySalaryModesQuery = (enabled: boolean = true) => {
    const { company } = useUser();
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.ZENEGY_SALARY_MODES, company?.companyId],
        queryFn: paymentsService.getZenegySalaryModes,
        enabled: !!company?.companyId && enabled
    });
};

export const useZenegySalaryTypesQuery = (enabled: boolean = true) => {
    const { company } = useUser();
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.ZENEGY_SALARY_TYPES, company?.companyId],
        queryFn: paymentsService.getZenegySalaryTypes,
        enabled: !!company?.companyId && enabled
    });
};

export const useZenegyEmployeeTypesQuery = (enabled: boolean = true) => {
    const { company } = useUser();
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.ZENEGY_EMPLOYEE_TYPES,
            company?.companyId
        ],
        queryFn: paymentsService.getZenegyEmployeeTypes,
        enabled: !!company?.companyId && enabled
    });
};

export const useIntectDepartmentsQuery = (enabled: boolean = true) => {
    const { company } = useUser();
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.INTECT_DEPARTMENTS, company?.companyId],
        queryFn: paymentsService.getIntectDepartments,
        enabled: !!company?.companyId && enabled
    });
};

export const useIntectEmploymentTemplatesQuery = (enabled: boolean = true) => {
    const { company } = useUser();
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.INTECT_EMPLOYMENT_TEMPLATES,
            company?.companyId
        ],
        queryFn: paymentsService.getIntectEmploymentTemplates,
        enabled: !!company?.companyId && enabled
    });
};

export const useGetUpcomingShifts = (userId: string, enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.GET_UPCOMING_SHIFTS, userId],
        queryFn: async () => {
            const response = await paymentsService.getUpcomingShifts(userId);
            return response.data.currentPeriodShifts as CurrentPeriodShifts;
        },
        enabled: !!userId && enabled
    });

export const useGetAvailableShifts = (
    userId: string,
    enabled: boolean = true
) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.GET_AVAILABLE_SHIFTS, userId],
        queryFn: async () => {
            const response = await paymentsService.getAvailableShifts(userId);
            return response.data.availableShifts as AvailableShifts;
        },
        enabled: !!userId && enabled
    });
